import { useMemo } from 'react'
import { Heading, Text, CSSObject } from '@ritualco/jutsu'
import queryString from 'query-string'
import { CardPage } from 'components/PageTemplates'
import {
  WorkspaceSelector,
  WorkspaceChangeProps,
} from 'components/WorkspaceSelector'
import { useContent } from 'lib/content/react'
import { useLocation } from 'lib/router'
import { mapToWorkspaceList } from 'api/mappers/workspace'
import { OrganizationType, WorkspacesResponse } from 'lib/generated/api'
import { useGetOrganizationsWorkspaces } from 'api/endpoints/workspaces'
import { useStore } from 'state/store'
import { UseQueryResult } from 'react-query'
import { useGetWorkspaceInvite } from 'api/endpoints/connections'

export type WorkspaceSwitcherProps = {
  onWorkspaceSelect?: (ids: WorkspaceChangeProps) => void
}

const textGenericStyles: CSSObject = {
  color: 'grayscale.primary',
  fontSize: 'small',
  textAlign: 'center',
}

const ConnectionsSelector = ({
  onWorkspaceSelect = () => {},
}: WorkspaceSwitcherProps) => {
  const { formatMessage } = useContent()

  const userName = useStore(state => state.userInfo.firstName)
  const userEmail = useStore(state => state.userInfo.email)
  const organizations = useStore(state => state.attachedOrganizations).filter(
    org => org.type === OrganizationType.Company
  )
  const workspacesQueries = useGetOrganizationsWorkspaces(
    organizations.map(org => ({
      organizationId: org.id,
      limit: 100,
    }))
  ) as UseQueryResult<WorkspacesResponse, any>[]
  const workspaces = useMemo(() => {
    return workspacesQueries
      .map(response => {
        const { data: workspaceResponse } = response
        return workspaceResponse?.results.length
          ? mapToWorkspaceList(workspaceResponse)
          : []
      })
      .reduce((accumulator, item) => {
        return item.length ? accumulator.concat(item) : accumulator
      }, [])
  }, [workspacesQueries])
  const { connectionId: id } = queryString.parse(useLocation().search)
  const connectionId = (id as string) || ''
  const workspaceInvite = useGetWorkspaceInvite({ connectionId })

  return (
    <CardPage>
      <Text sx={textGenericStyles} mb={4}>
        {formatMessage(
          {
            id: 'pmportal.connections.switcher.welcome',
          },
          {
            name: userName ? `, ${userName}` : '',
          }
        )}
      </Text>
      <Heading fontSize="3xlarge" as="h1" textAlign="center">
        {formatMessage(
          {
            id: 'pmportal.connections.switcher.header',
          },
          {
            organization: `${workspaceInvite?.data?.inviterOrganizationName}`,
            workspace: `${workspaceInvite?.data?.inviterWorkspaceName}`,
          }
        )}
      </Heading>
      <Text fontSize="small" mt={8} mb={8}>
        {formatMessage(
          {
            id: 'pmportal.connections.userIdentifier',
          },
          {
            email: () => (
              <Text as="span" fontWeight="medium">
                {userEmail}
              </Text>
            ),
          }
        )}
      </Text>
      <WorkspaceSelector
        workspaces={workspaces}
        organizations={organizations}
        onWorkspaceChange={onWorkspaceSelect}
      />
    </CardPage>
  )
}

export default ConnectionsSelector
